// Lucide icons used by the Pencil design (icon set "lucide"), inlined to avoid a dependency.
// 24x24 grid, 2px round strokes. Decorative by default (aria-hidden).

import { Fragment, type ReactElement } from 'react'

const P = (d: string) => <path d={d} />

const ICONS = {
  'message-circle': [P('M7.9 20A9 9 0 1 0 4 16.1L2 22Z')],
  'message-square': [P('M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z')],
  'layout-dashboard': [
    <rect key="a" width="7" height="9" x="3" y="3" rx="1" />,
    <rect key="b" width="7" height="5" x="14" y="3" rx="1" />,
    <rect key="c" width="7" height="9" x="14" y="12" rx="1" />,
    <rect key="d" width="7" height="5" x="3" y="16" rx="1" />,
  ],
  repeat: [P('m17 2 4 4-4 4'), P('M3 11v-1a4 4 0 0 1 4-4h14'), P('m7 22-4-4 4-4'), P('M21 13v1a4 4 0 0 1-4 4H3')],
  'building-2': [
    P('M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z'),
    P('M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2'),
    P('M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2'),
    P('M10 6h4'),
    P('M10 10h4'),
    P('M10 14h4'),
    P('M10 18h4'),
  ],
  'book-open': [P('M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z'), P('M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z')],
  sliders: [P('M21 4h-7'), P('M10 4H3'), P('M21 12h-9'), P('M8 12H3'), P('M21 20h-5'), P('M12 20H3'), P('M14 2v4'), P('M8 10v4'), P('M16 18v4')],
  smartphone: [<rect key="a" width="14" height="20" x="5" y="2" rx="2" ry="2" />, P('M12 18h.01')],
  plug: [P('M12 22v-5'), P('M9 8V2'), P('M15 8V2'), P('M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z')],
  'credit-card': [<rect key="a" width="20" height="14" x="2" y="5" rx="2" />, P('M2 10h20')],
  server: [
    <rect key="a" width="20" height="8" x="2" y="2" rx="2" ry="2" />,
    <rect key="b" width="20" height="8" x="2" y="14" rx="2" ry="2" />,
    P('M6 6h.01'),
    P('M6 18h.01'),
  ],
  sparkles: [
    P(
      'M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z',
    ),
    P('M20 3v4'),
    P('M22 5h-4'),
    P('M4 17v2'),
    P('M5 18H3'),
  ],
  check: [P('M20 6 9 17l-5-5')],
  zap: [
    P(
      'M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z',
    ),
  ],
  hand: [
    P('M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2'),
    P('M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2'),
    P('M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8'),
    P('M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15'),
  ],
  play: [<polygon key="a" points="6 3 20 12 6 21 6 3" />],
  'calendar-check': [
    P('M8 2v4'),
    P('M16 2v4'),
    <rect key="a" width="18" height="18" x="3" y="4" rx="2" />,
    P('M3 10h18'),
    P('m9 16 2 2 4-4'),
  ],
  'shield-check': [
    P(
      'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    ),
    P('m9 12 2 2 4-4'),
  ],
  'message-square-off': [P('M21 15V5a2 2 0 0 0-2-2H9'), P('m2 2 20 20'), P('M3.6 3.6c-.4.3-.6.8-.6 1.4v16l4-4h10')],
  lock: [<rect key="a" width="18" height="11" x="3" y="11" rx="2" ry="2" />, P('M7 11V7a5 5 0 0 1 10 0v4')],
  timer: [P('M10 2h4'), P('M12 14l3-3'), <circle key="a" cx="12" cy="14" r="8" />],
  search: [<circle key="a" cx="11" cy="11" r="8" />, P('m21 21-4.3-4.3')],
  plus: [P('M5 12h14'), P('M12 5v14')],
  minus: [P('M5 12h14')],
  'chevron-right': [P('m9 18 6-6-6-6')],
  'chevron-left': [P('m15 18-6-6 6-6')],
  'chevron-down': [P('m6 9 6 6 6-6')],
  send: [
    P('M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z'),
    P('m21.854 2.147-10.94 10.939'),
  ],
  bot: [P('M12 8V4H8'), <rect key="a" width="16" height="12" x="4" y="8" rx="2" />, P('M2 14h2'), P('M20 14h2'), P('M15 13v2'), P('M9 13v2')],
  x: [P('M18 6 6 18'), P('m6 6 12 12')],
  menu: [P('M4 12h16'), P('M4 6h16'), P('M4 18h16')],
  pause: [<rect key="a" x="14" y="4" width="4" height="16" rx="1" />, <rect key="b" x="6" y="4" width="4" height="16" rx="1" />],
  'alert-triangle': [P('m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3'), P('M12 9v4'), P('M12 17h.01')],
  refresh: [
    P('M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8'),
    P('M21 3v5h-5'),
    P('M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16'),
    P('M8 16H3v5'),
  ],
  'log-out': [P('M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4'), P('m16 17 5-5-5-5'), P('M21 12H9')],
  trash: [P('M3 6h18'), P('M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6'), P('M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2')],
  pencil: [
    P(
      'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
    ),
  ],
  'arrow-right': [P('M5 12h14'), P('m12 5 7 7-7 7')],
  'arrow-left': [P('M19 12H5'), P('m12 19-7-7 7-7')],
  mail: [<rect key="a" width="20" height="16" x="2" y="4" rx="2" />, P('m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7')],
  download: [P('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'), P('m7 10 5 5 5-5'), P('M12 15V3')],
  users: [
    P('M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2'),
    <circle key="a" cx="9" cy="7" r="4" />,
    P('M22 21v-2a4 4 0 0 0-3-3.87'),
    P('M16 3.13a4 4 0 0 1 0 7.75'),
  ],
  loader: [P('M21 12a9 9 0 1 1-6.219-8.56')],
  globe: [<circle key="a" cx="12" cy="12" r="10" />, P('M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20'), P('M2 12h20')],
  clock: [<circle key="a" cx="12" cy="12" r="10" />, P('M12 6v6l4 2')],
  'circle-check': [<circle key="a" cx="12" cy="12" r="10" />, P('m9 12 2 2 4-4')],
  'thumbs-up': [
    P('M7 10v12'),
    P('M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z'),
  ],
  'thumbs-down': [
    P('M17 14V2'),
    P('M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z'),
  ],
  'circle-help': [<circle key="a" cx="12" cy="12" r="10" />, P('M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3'), P('M12 17h.01')],
  'external-link': [P('M15 3h6v6'), P('M10 14 21 3'), P('M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6')],
  activity: [P('M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2')],
  phone: [
    P(
      'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z',
    ),
  ],
  facebook: [P('M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z')],
  instagram: [
    <rect key="a" width="20" height="20" x="2" y="2" rx="5" ry="5" />,
    P('M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z'),
    P('M17.5 6.5h.01'),
  ],
  // WhatsApp-style bubble with a handset, drawn on the Lucide grid.
  whatsapp: [P('M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21'), P('M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1')],
  // Messenger-style bubble with the lightning zigzag.
  messenger: [
    P('M12 2C6.5 2 2 6.1 2 11.2c0 2.9 1.5 5.5 3.8 7.2V22l3.5-1.9c.9.3 1.8.4 2.7.4 5.5 0 10-4.1 10-9.2S17.5 2 12 2z'),
    P('m7 13 3.5-3.5 3 3L17 9'),
  ],
  'message-circle-reply': [P('M7.9 20A9 9 0 1 0 4 16.1L2 22Z'), P('m10 15-3-3 3-3'), P('M7 12h7a2 2 0 0 1 2 2v1')],
  'messages-square': [P('M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z'), P('M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1')],
  unlink: [
    P('m18.84 12.25 1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71'),
    P('m5.17 11.75-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71'),
    P('M8 2v3'),
    P('M2 8h3'),
    P('M16 19v3'),
    P('M19 16h3'),
  ],
  lightbulb: [
    P('M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5'),
    P('M9 18h6'),
    P('M10 22h4'),
  ],
} satisfies Record<string, ReactElement[]>

export type IconName = keyof typeof ICONS

interface IconProps {
  name: IconName
  size?: number
  className?: string
  strokeWidth?: number
  /** Accessible label. Without it the icon is hidden from assistive tech. */
  label?: string
}

export function Icon({ name, size = 16, className, strokeWidth = 2, label }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
      focusable="false"
    >
      {ICONS[name].map((el, i) => (
        <Fragment key={i}>{el}</Fragment>
      ))}
    </svg>
  )
}
